import { useEffect, useMemo } from 'react';
import { readClientNotes, reloadClientNotes, useClientNotes } from '../Advisor/ClientV2/clientNotesStore';
import {
  readClientQuizzes,
  readClientQuizzesFailed,
  removeQuiz,
  stampQuizApplied,
  useClientQuizzes
} from '../../store/clientQuiz/cache';
import type { AdviserQuiz, ClientQuizList, QuizType } from '../../store/clientQuiz/api';
import type { CoverQuizAnswers } from './coverQuizData';

/**
 * A finished 保障小測驗, as the adviser's readers see it: the completed
 * ClientQuiz row of the client's factsheet (docs/specs/2026-09-24-demo-localstorage-to-db-design.md
 * §5, §17), read through the shared per-factsheet cache in store/clientQuiz/cache.
 */

export type CoverQuizResult = {
  /** 2 since the persona question and the chosen question set; 1 is not read. */
  version: 2;
  clientId: string;
  /** The ClientQuiz row this result came from. */
  id?: string;
  /** The invitation's display name (what the adviser sent it with), if any. */
  name?: string;
  answers: CoverQuizAnswers;
  completedAt: string;
  /** Set when the adviser copied the quiz's DISC letter into the factsheet. */
  discAppliedAt?: string;
  appliedAt?: string;
  /** Set when 第二部分 (理財目標) was added to an already completed quiz. */
  goalsAddedAt?: string;
};

/** A link opened without a client token (a preview) saves nowhere the adviser reads. */
export const GUEST_CLIENT_ID = 'guest';

/** Maps a completed ClientQuiz row to the result shape the readers use; exported so
 * CoverQuizDialog can build it from `useQuizInvitations`'s own `list` without a second,
 * separately-polled subscription. */
export const coverResultOf = (
  clientId: string | null | undefined,
  row: AdviserQuiz | null | undefined
): CoverQuizResult | null =>
  clientId && row && row.answers
    ? {
        version: 2,
        clientId,
        id: row.id,
        name: row.displayName || undefined,
        answers: row.answers as CoverQuizAnswers,
        completedAt: row.submittedAt || row.createdAt,
        discAppliedAt: row.discAppliedAt,
        appliedAt: row.appliedAt,
        goalsAddedAt: row.goalsAddedAt
      }
    : null;

// Re-reads already asked for, so a server that disagrees with itself cannot
// turn the 5 s poll into a re-read every 5 s.
const reloadedFor = new Set<string>();

/** The dialogs' view: the list (completed + pending per part), the factsheet it belongs to, and
 * `failed` when the last refresh did not arrive (what is shown may be out of date).
 * `load: false` reads the cache only (a mounted-but-closed dialog asks the server nothing).
 *
 * The client list's own signals of a finished quiz and of a live part-1 link are
 * `quizCompletedAt` and `quizPendingUntil`, which arrive with the client, not with
 * the quiz list. When the list shows a result or a live link the client does not
 * know about (or the reverse), the client is re-read once, so the list's tiles and
 * 今日招募 follow what the dialog already shows. */
export const useQuizInvitations = (
  clientId: string | null | undefined,
  type: QuizType,
  options: { pollMs?: number; load?: boolean } = {}
) => {
  const notes = useClientNotes(clientId);
  const factsheetId = notes?.factsheetId || null;
  const list: ClientQuizList | null = useClientQuizzes(factsheetId, type, options);
  const completedId = list ? list.completed?.id || 'none' : null;
  const knownCompleted = Boolean(notes?.quizCompletedAt?.[type]);
  const livePartOne = list ? (list.pending || []).find(row => row.part === 1 && !row.expired) : undefined;
  const pendingId = list ? livePartOne?.id || 'none' : null;
  const pendingUntil = notes?.quizPendingUntil?.[type];
  const knownPending = Boolean(pendingUntil && new Date(pendingUntil).getTime() > Date.now());
  useEffect(() => {
    if (!clientId || completedId === null) return;
    const keys = [
      (completedId !== 'none') !== knownCompleted && `${clientId}:${type}:${completedId}`,
      (pendingId !== 'none') !== knownPending && `${clientId}:${type}:pending:${pendingId}`
    ].filter((key): key is string => Boolean(key) && !reloadedFor.has(key as string));
    if (!keys.length) return;
    keys.forEach(key => reloadedFor.add(key));
    reloadClientNotes(clientId);
  }, [clientId, type, completedId, knownCompleted, pendingId, knownPending]);
  // Read on the same render the list changes on: a failure is announced like a load.
  const failed = readClientQuizzesFailed(factsheetId, type);
  return { list, factsheetId, failed };
};

export const readCoverQuiz = (clientId?: string | null): CoverQuizResult | null =>
  coverResultOf(clientId, readClientQuizzes(readClientNotes(clientId)?.factsheetId, 'cover')?.completed);

export const useCoverQuiz = (clientId?: string | null, options: { pollMs?: number } = {}): CoverQuizResult | null => {
  const { list } = useQuizInvitations(clientId, 'cover', options);
  return useMemo(() => coverResultOf(clientId, list?.completed), [clientId, list]);
};

export const clearCoverQuiz = async (clientId: string): Promise<boolean> => {
  const factsheetId = readClientNotes(clientId)?.factsheetId;
  const row = readClientQuizzes(factsheetId, 'cover')?.completed;
  if (!factsheetId || !row) return false;
  const cleared = await removeQuiz(factsheetId, 'cover', row.id, 'completed');
  // `quizCompletedAt` (the client-notes copy of when this was completed) only a
  // client re-read refreshes — the quiz cache refresh above doesn't touch it.
  if (cleared) reloadClientNotes(clientId);
  return cleared;
};

export const markCoverQuizDiscApplied = (clientId: string): void => {
  const factsheetId = readClientNotes(clientId)?.factsheetId;
  const row = readClientQuizzes(factsheetId, 'cover')?.completed;
  if (factsheetId && row) void stampQuizApplied(factsheetId, 'cover', row.id, true).catch(() => undefined);
};

/** 匯入客戶資料 was used: stamps the result's `appliedAt`, from whichever entry point imported it. */
export const markCoverQuizApplied = (clientId: string): void => {
  const factsheetId = readClientNotes(clientId)?.factsheetId;
  const row = readClientQuizzes(factsheetId, 'cover')?.completed;
  if (factsheetId && row) void stampQuizApplied(factsheetId, 'cover', row.id).catch(() => undefined);
};

/** Which parts of the quiz a stored result holds. 第二部分 can be sent on its own. */
export const coverQuizPartsDone = (result: CoverQuizResult | null) => ({
  one: Boolean(result && Object.keys(result.answers.picks || {}).length),
  two: Boolean(result && result.answers.goals?.picked.length)
});

export type CoverQuizPart = 1 | 2;

/**
 * `?t=<token>` is a real invitation; anything else is a guest preview. The page
 * gets `?t=` from the short link (/b/<token>, /r/<token>, ShortQuizLink).
 */
export const parseQuizLink = (search: string): { token: string | null } => {
  const token = (new URLSearchParams(search || '').get('t') || '').trim();
  return { token: token || null };
};
